import { normalizeSearch, packRibbonLanes } from '@app/hr/hr-leave-board';
import { isoWeekdayOf } from '@app/hr/hr-schedule-engine';
import type { RentalInterruption } from '@app/shared/_models/sales';
import { businessDayOf } from '@app/shared/time';
import type { Day, DayRange, DayState, DayStateRange, DueEntry, CreditEntry, RankSpan, RentalLedgerEntry, RentalLifecycleFull, RentalLineView } from './rental-billing.model';
import { addDays, addMonthsClamped, daysBetween, firstOfMonth, frMonth, frMonthShort, frShort, maxDay, minDay } from './rental-days';

// Géométrie du Planning : positions et sélections sur les données du serveur, aucun état de facturation calculé ici.
// Toutes les plages sont start / end, premier et dernier jour INCLUS (fenêtre affichée comprise) ; seul le calcul en pourcentage
// a besoin du bord droit d'un jour, donné par dayEndPct (bord gauche du lendemain), jamais stocké dans une plage.
export interface DayWindow { start: Day; end: Day; } // jours affichés, le premier et le dernier inclus
export type WindowPreset = 'default' | '1m' | '3m' | '6m' | '12m' | 'all';

// Nombre de jours affichés, le premier et le dernier compris.
export function windowDays(w: DayWindow): number {
  return Math.max(1, daysBetween(w.start, w.end) + 1);
}

// Bord gauche du jour, en % de la fenêtre.
export function dayPct(day: Day, w: DayWindow): number {
  return (daysBetween(w.start, day) / windowDays(w)) * 100;
}

// Bord droit du jour (= bord gauche du lendemain), en % de la fenêtre : la fin d'une barre dont ce jour est le dernier.
export function dayEndPct(day: Day, w: DayWindow): number {
  return dayPct(addDays(day, 1), w);
}

export function dayAtX(x: number, width: number, w: DayWindow): Day {
  const n = windowDays(w);
  const idx = width > 0 ? Math.floor((x / width) * n + 1e-9) : 0;
  return addDays(w.start, Math.min(n - 1, Math.max(0, idx)));
}

export function pxPerDay(width: number, w: DayWindow): number {
  return width / windowDays(w);
}

// k mois calés sur des 1ers de mois (du 1er du premier mois au dernier jour du dernier) ; par défaut 60 jours avant as_of et 29 après.
export function windowPreset(kind: WindowPreset, asOf: Day, bounds?: DayWindow | null): DayWindow {
  const months: Record<string, number> = { '1m': 1, '3m': 3, '6m': 6, '12m': 12 };
  const k = months[kind];
  if (k) {
    const start = addMonthsClamped(firstOfMonth(asOf), -Math.floor((k - 1) / 2));
    return { start, end: addDays(addMonthsClamped(start, k), -1) };
  }
  if (kind === 'all' && bounds) return { start: bounds.start, end: bounds.end };
  return { start: addDays(asOf, -60), end: addDays(asOf, 29) };
}

// Une fenêtre calée sur des mois entiers (du 1er au dernier jour d'un mois) le reste ; une autre garde sa longueur.
export function shiftWindow(w: DayWindow, months: number): DayWindow {
  const start = addMonthsClamped(w.start, months);
  const aligned = w.start.endsWith('-01') && addDays(w.end, 1).endsWith('-01');
  return { start, end: aligned ? addDays(addMonthsClamped(addDays(w.end, 1), months), -1) : addDays(start, windowDays(w) - 1) };
}

// Jour de Paris de la livraison ou de la reprise : realdatdel / return_realdatdel sont l'instant réel du BL, heure comprise (businessDayOf).
export function deliveredDay(l: { realdatdel: string | null }): Day | null {
  return businessDayOf(l.realdatdel);
}

export function returnedDay(l: { return_realdatdel: string | null }): Day | null {
  return businessDayOf(l.return_realdatdel);
}

// Du plus ancien jour connu (frise, livraison, décision) au dernier jour de la frise, au moins 30 jours après as_of.
export function knownBounds(v: RentalLifecycleFull): DayWindow | null {
  const starts: (Day | null | undefined)[] = [deliveredDay(v)];
  let lastEnd: Day | null = null;
  for (const l of v.lines ?? []) {
    starts.push(l.days[0]?.start, deliveredDay(l), l.planned_on);
    lastEnd = maxDay(lastEnd, l.days[l.days.length - 1]?.end);
    const d = l.decisions;
    if (!d) continue;
    for (const i of d.interruptions) starts.push(i.start);
    for (const x of d.window) starts.push(x.day);
    for (const p of d.prescriptions) starts.push(p.start);
  }
  const start = minDay(...starts);
  if (!start) return null;
  const end = maxDay(lastEnd, addDays(v.as_of, 30)) as Day;
  return { start, end: end >= start ? end : start };
}

export interface MonthTick { first: Day; label: string; short: string; left: number; width: number; }

export function monthTicksOfWindow(w: DayWindow): MonthTick[] {
  const ticks: MonthTick[] = [];
  for (let first = firstOfMonth(w.start); first <= w.end; first = addMonthsClamped(first, 1)) {
    const start = maxDay(first, w.start) as Day;
    const end = minDay(addDays(addMonthsClamped(first, 1), -1), w.end) as Day; // dernier jour du mois, ou de la fenêtre
    const left = dayPct(start, w);
    ticks.push({ first, label: frMonth(first), short: frMonthShort(first), left, width: dayEndPct(end, w) - left });
  }
  return ticks;
}

// Largeur moyenne d'un caractère à 12 px : 7,2 px, plus 12 px de marge.
export function monthLabelFor(t: MonthTick, trackPx: number): string {
  const px = (t.width / 100) * trackPx;
  if (px >= t.label.length * 7.2 + 12) return t.label;
  if (px >= t.short.length * 7.2 + 12) return t.short;
  return '';
}

export interface DayTick { day: Day; label: string; left: number; today: boolean; }

export function dayTicksOfWindow(w: DayWindow, asOf: Day, ppd: number): DayTick[] {
  const all = ppd >= 12;
  const firstMondayOnly = !all && 7 * ppd < 16;
  const ticks: DayTick[] = [];
  for (let d = w.start; d <= w.end; d = addDays(d, 1)) {
    const monday = isoWeekdayOf(d) === 1;
    const keep = all || d === asOf || (monday && (!firstMondayOnly || Number(d.slice(8, 10)) <= 7));
    if (!keep) continue;
    ticks.push({ day: d, label: all ? String(Number(d.slice(8, 10))) : frShort(d), left: dayPct(d, w), today: d === asOf });
  }
  return ticks;
}

export function monthGridCssOfWindow(w: DayWindow, color = '#e2e6ef'): string {
  const stops = monthTicksOfWindow(w)
    .filter((t) => t.left > 0)
    .flatMap((t) => {
      const p = round4(t.left);
      return [`transparent ${p}%`, `${color} ${p}%`, `${color} calc(${p}% + 1px)`, `transparent calc(${p}% + 1px)`];
    });
  return stops.length ? 'linear-gradient(to right, ' + stops.join(', ') + ')' : 'none';
}

// Motif de 7 jours répété depuis w.start : les samedis et dimanches sont placés dans le motif même (aucun décalage de position).
export function weekendGridCss(w: DayWindow, color = 'var(--weekend)'): { image: string; size: string; position: string } {
  const startWd = isoWeekdayOf(w.start);
  const stops: string[] = [];
  for (let i = 0; i < 7; i++) {
    const wd = ((startWd - 1 + i) % 7) + 1;
    const c = wd >= 6 ? color : 'transparent';
    stops.push(`${c} ${round4((i / 7) * 100)}%`, `${c} ${round4(((i + 1) / 7) * 100)}%`);
  }
  return { image: 'linear-gradient(to right, ' + stops.join(', ') + ')', size: `${round4((700 / windowDays(w)))}% 100%`, position: '0 0' };
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

export interface Bar { left: number; width: number; clippedStart: boolean; clippedEnd: boolean; }

// Barre des jours start à end, les deux inclus, rognée à la fenêtre ; null si la plage est vide ou hors de la fenêtre.
export function spanBar(start: Day, end: Day, w: DayWindow): Bar | null {
  if (end < start || end < w.start || start > w.end) return null;
  const a = start < w.start ? w.start : start;
  const b = end > w.end ? w.end : end;
  const left = dayPct(a, w);
  return { left, width: dayEndPct(b, w) - left, clippedStart: start < w.start, clippedEnd: end > w.end };
}

export interface StateSegment extends Bar { start: Day; end: Day; state: DayState; fut: boolean; }
const CUT_AT_TODAY: ReadonlySet<DayState> = new Set<DayState>(['RUNNING', 'DUE', 'INTERRUPTED']);
const AFTER_RETURN: ReadonlySet<DayState> = new Set<DayState>(['UPCOMING', 'RETURNED']);

// Séries du serveur bornées à la fenêtre, coupées au trait rouge (début d'as_of, JP 02/11) : aujourd'hui est après lui, sa part RUNNING ou DUE
// (current_day_due) se dessine UPCOMING, rien d'« en cours » ni d'« à facturer » au-delà du trait ; une interruption y est estompée.
export function stateSegmentsOf(days: DayStateRange[], w: DayWindow, asOf: Day, returned = false): StateSegment[] {
  const ranges: { start: Day; end: Day; state: DayState; fut: boolean }[] = [];
  const add = (start: Day, end: Day, state: DayState, fut: boolean) => {
    if (end < start) return;
    const last = ranges[ranges.length - 1];
    if (last && last.state === state && last.fut === fut && addDays(last.end, 1) === start) last.end = end;
    else ranges.push({ start, end, state, fut });
  };
  for (const r of days ?? []) {
    if (r.state === 'OUTSIDE') continue;
    if (!CUT_AT_TODAY.has(r.state)) {
      add(r.start, r.end, r.state, false);
      continue;
    }
    add(r.start, minDay(r.end, addDays(asOf, -1)) as Day, r.state, false);
    add(maxDay(r.start, asOf) as Day, r.end, r.state === 'INTERRUPTED' ? r.state : 'UPCOMING', r.state === 'INTERRUPTED');
  }
  const out: StateSegment[] = [];
  for (const r of ranges) {
    // returned : BL de reprise signé ; ni « À venir » ni « Repris » à droite du trait, la suite d'un matériel repris ne veut rien dire (JP 02/11).
    const end = returned && AFTER_RETURN.has(r.state) ? minDay(r.end, addDays(asOf, -1)) as Day : r.end;
    const bar = spanBar(r.start, end, w);
    if (bar) out.push({ ...bar, start: maxDay(r.start, w.start) as Day, end: minDay(end, w.end) as Day, state: r.state, fut: r.fut });
  }
  return out;
}

// État d'un jour tel que dessiné par stateSegmentsOf : RUNNING ou DUE à partir d'as_of (après le trait rouge) se montre UPCOMING ;
// d'un matériel repris, rien à droite du trait (null), comme le dessin.
export function shownStateAt(l: RentalLineView, day: Day, asOf: Day): DayState | null {
  const s = stateAt(l, day);
  const shown = (s === 'RUNNING' || s === 'DUE') && day >= asOf ? 'UPCOMING' : s;
  return shown && day >= asOf && AFTER_RETURN.has(shown) && returnedDay(l) ? null : shown;
}

// Présence du matériel : du jour de livraison à la veille de la reprise, inclus ; end null = encore en place. Comme le trait rouge, livraison
// et reprise sont au début de leur jour (JP 02/11) : la barre s'arrête au dos de la flèche de reprise, et au dernier jour facturé (E = veille de R).
export function presenceRange(l: RentalLineView): { start: Day; end: Day | null } | null {
  const start = deliveredDay(l);
  if (!start) return null;
  const r = returnedDay(l);
  return { start, end: r ? addDays(r, -1) : null };
}

export function presenceBarOf(l: RentalLineView, w: DayWindow, _asOf: Day): (Bar & { open: boolean }) | null {
  const r = presenceRange(l);
  if (!r) return null;
  const bar = spanBar(r.start, r.end ?? w.end, w);
  return bar ? { ...bar, open: r.end === null } : null;
}

// Jours OUTSIDE pendant la présence : matériel en place, non facturé (Durée atteinte, hors prescription).
export function outsidePresentOf(l: RentalLineView, w: DayWindow): (Bar & DayRange)[] {
  const p = presenceRange(l);
  if (!p) return [];
  const out: (Bar & DayRange)[] = [];
  for (const r of l.days ?? []) {
    if (r.state !== 'OUTSIDE') continue;
    const start = maxDay(r.start, p.start) as Day;
    const end = p.end === null ? r.end : (minDay(r.end, p.end) as Day);
    const bar = spanBar(start, end, w);
    if (bar) out.push({ ...bar, start: maxDay(start, w.start) as Day, end: minDay(end, w.end) as Day });
  }
  return out;
}

// day = jour sur lequel le repère est dessiné. Le repère de fin est dessiné sur le premier jour NON facturé, lendemain de summary.window_end
// (dernier jour facturé, inclus) : même place qu'avant le 27/10/2026, ce lendemain n'est qu'une position à l'écran.
// Livraisons et reprises, prévues ou réalisées : flèches des BL (blArrowsOf), plus des repères (30/10/2026, JP).
export interface Marker { kind: 'start' | 'end'; day: Day; left: number; }

export function markersOf(l: RentalLineView, w: DayWindow, lines: RentalLineView[]): Marker[] {
  const out: Marker[] = [];
  const add = (kind: Marker['kind'], day: Day | null | undefined) => {
    if (day && day >= w.start && day <= w.end) out.push({ kind, day, left: dayPct(day, w) });
  };
  const delivered = deliveredDay(l);
  const s = l.summary;
  if (s?.window_start && delivered && s.window_start < delivered) add('start', s.window_start);
  if (s?.window_end && l.decisions?.effective_end_id != null) add('end', addDays(s.window_end, 1));
  return out;
}

// BL qui porte la ligne (lnk_deliveries du dossier) : pose (+) ou reprise (−1) ; askdatdel est un jour, realdatdel l'instant de signature.
export interface BlRef { id: number; kind: 'delivery' | 'return'; realdatdel: string | null; askdatdel: string | null; }
// left : base (dos) de la flèche, prévue ou réalisée, au début de son jour comme le trait rouge (JP 02/11) : sur le trait si c'est aujourd'hui,
// collée au départ (livraison) ou au bout (reprise) du trait de présence. ▶ s'étend à droite de sa base, ◀ à gauche.
export interface BlArrow { id: number; kind: BlRef['kind']; done: boolean; day: Day; left: number; }

// Une flèche par BL et par sens ; signé = jour de Paris de la signature, sinon jour demandé ; sans jour (date à fixer) ou hors de la frise, rien.
export function blArrowsOf(bls: readonly BlRef[], w: DayWindow): BlArrow[] {
  const out: BlArrow[] = [];
  const seen = new Set<string>();
  for (const b of bls) {
    const done = !!b.realdatdel;
    const day = done ? businessDayOf(b.realdatdel) : b.askdatdel ? String(b.askdatdel).slice(0, 10) : null;
    const key = b.id + '|' + b.kind;
    if (!day || day < w.start || day > w.end || seen.has(key)) continue;
    seen.add(key);
    out.push({ id: b.id, kind: b.kind, done, day, left: dayPct(day, w) });
  }
  return out;
}

export interface LedgerBar extends Bar { entry: RentalLedgerEntry; credit: boolean; }

export function ledgerBarsOf(l: RentalLineView, w: DayWindow): LedgerBar[] {
  const out: LedgerBar[] = [];
  for (const entry of l.ledger ?? []) {
    const bar = spanBar(entry.start, entry.end, w);
    if (bar) out.push({ ...bar, entry, credit: entry.sign < 0 || entry.doc_type === 'AVOIR' });
  }
  return out;
}

// Traits de début de rang (premier jour de chaque période facturée ou due) et de fin de la période due à summary.next_due_on :
// son lendemain si le jour du calcul est échu (currentDayDue, défaut serveur), ce jour même sinon ; rien si le paramètre est illisible.
export function periodTicksOf(l: RentalLineView, w: DayWindow, currentDayDue: boolean | null): number[] {
  const days = new Set<Day>();
  for (const e of l.ledger ?? []) for (const p of e.periods ?? []) days.add(p.start);
  for (const e of l.due ?? []) for (const p of e.periods ?? []) days.add(p.start);
  const next = l.summary?.next_due_on;
  if (next && currentDayDue !== null) days.add(currentDayDue ? addDays(next, 1) : next);
  return [...days].filter((d) => d >= w.start && d <= w.end).sort().map((d) => dayPct(d, w));
}

export function prescriptionBarsOf(l: RentalLineView, w: DayWindow): (Bar & { id: number })[] {
  const out: (Bar & { id: number })[] = [];
  for (const p of l.decisions?.prescriptions ?? []) {
    if (p.cancelled) continue;
    const bar = spanBar(p.start, p.end, w);
    if (bar) out.push({ ...bar, id: p.id });
  }
  return out;
}

function inRanges(ranges: DayRange[] | null | undefined, day: Day): boolean {
  return (ranges ?? []).some((r) => r.start <= day && day <= r.end);
}

export function stateAt(l: RentalLineView, day: Day): DayState | null {
  return (l.days ?? []).find((r) => r.start <= day && day <= r.end)?.state ?? null;
}

// Pièces dont les jours payés contiennent le jour ; à défaut celles dont les rangs le couvrent (PAID_INTERRUPTED en grille) ; factures puis avoirs.
export function ledgerAt(l: RentalLineView, day: Day): RentalLedgerEntry[] {
  const ledger = l.ledger ?? [];
  let found = ledger.filter((e) => inRanges(e.days, day));
  if (!found.length) found = ledger.filter((e) => e.start <= day && day <= e.end);
  return [...found.filter((e) => e.sign > 0), ...found.filter((e) => e.sign <= 0)];
}

export function dueAt(l: RentalLineView, day: Day): DueEntry | null {
  const due = l.due ?? [];
  return due.find((e) => inRanges(e.days, day)) ?? due.find((e) => e.start <= day && day <= e.end) ?? null;
}

export function creditAt(l: RentalLineView, day: Day): CreditEntry | null {
  return (l.credits ?? []).find((e) => inRanges(e.days, day)) ?? null;
}

// Jour INTERRUPTED lu dans les pièces : aucune facture ne le paie, une facture le paie et le report le rend, un avoir prévu (credits) ou émis (registre) le rend.
export type InterruptedKind = 'free' | 'carried' | 'credit_due' | 'credited';
const isCreditPiece = (e: RentalLedgerEntry) => e.sign < 0 || e.doc_type === 'AVOIR';

export function interruptedKindAt(l: RentalLineView, day: Day): InterruptedKind {
  const ledger = l.ledger ?? [];
  if (!ledger.some((e) => !isCreditPiece(e) && inRanges(e.days, day))) return 'free';
  if (ledger.some((e) => isCreditPiece(e) && inRanges(e.days, day))) return 'credited';
  return creditAt(l, day) ? 'credit_due' : 'carried';
}

// Avoirs émis dont les jours rendus contiennent le jour.
export function creditPiecesAt(l: RentalLineView, day: Day): RentalLedgerEntry[] {
  return (l.ledger ?? []).filter((e) => isCreditPiece(e) && inRanges(e.days, day));
}

// Jour payé devenu non dû hors interruption (décès, fin décidée, reprise, hors location, offert) : avoir prévu ou émis, sinon rien (JP 30/10).
export type CreditKind = 'credit_due' | 'credited';
export const CREDITABLE_STATES: ReadonlySet<DayState> = new Set<DayState>(['RETURNED', 'OUTSIDE', 'OFFERED', 'WAITING_START']);

export function creditKindAt(l: RentalLineView, day: Day): CreditKind | null {
  const ledger = l.ledger ?? [];
  if (!ledger.some((e) => !isCreditPiece(e) && inRanges(e.days, day))) return null;
  if (ledger.some((e) => isCreditPiece(e) && inRanges(e.days, day))) return 'credited';
  return creditAt(l, day) ? 'credit_due' : null;
}

// Segment coupé en séries de même nature (null = nature d'origine) ; seuls les bords du segment d'origine gardent leur coupure par la fenêtre.
function splitBy<K extends string>(s: StateSegment, w: DayWindow, kindAt: (d: Day) => K | null): (StateSegment & { kind: K | null })[] {
  const out: (StateSegment & { kind: K | null })[] = [];
  let start = s.start;
  let kind = kindAt(start);
  for (let d = s.start; d <= s.end; d = addDays(d, 1)) {
    const next = d < s.end ? addDays(d, 1) : null;
    const nextKind = next ? kindAt(next) : null;
    if (next && nextKind === kind) continue;
    const bar = spanBar(start, d, w)!;
    out.push({ ...bar, start, end: d, state: s.state, fut: s.fut, kind, clippedStart: start === s.start && s.clippedStart, clippedEnd: d === s.end && s.clippedEnd });
    if (next) { start = next; kind = nextKind; }
  }
  return out;
}

export function splitInterrupted(l: RentalLineView, s: StateSegment, w: DayWindow): (StateSegment & { kind: InterruptedKind })[] {
  return splitBy(s, w, (d) => interruptedKindAt(l, d)) as (StateSegment & { kind: InterruptedKind })[];
}

export function splitCredited(l: RentalLineView, s: StateSegment, w: DayWindow): (StateSegment & { kind?: CreditKind })[] {
  return splitBy(s, w, (d) => creditKindAt(l, d)).map(({ kind, ...seg }) => (kind ? { ...seg, kind } : seg));
}

export function rankSpanAt(l: RentalLineView, day: Day): (RankSpan & { source: 'ledger' | 'due' }) | null {
  for (const e of l.ledger ?? []) for (const p of e.periods ?? []) if (p.start <= day && day <= p.end) return { ...p, source: 'ledger' };
  for (const e of l.due ?? []) for (const p of e.periods ?? []) if (p.start <= day && day <= p.end) return { ...p, source: 'due' };
  return null;
}

// Jours reportés (carried[].day_count) cités par les pièces et par le dû, avec la pièce d'origine quand le registre la porte.
export function carriedNotesOf(l: RentalLineView): { docRef: string | null; dayCount: number; on: 'ledger' | 'due' }[] {
  const ledger = l.ledger ?? [];
  const refOf = (lineId: number) => ledger.find((e) => e.invoice_line_id === lineId)?.doc_ref ?? null;
  return [
    ...ledger.flatMap((e) => (e.carried ?? []).map((c) => ({ docRef: refOf(c.invoice_line_id), dayCount: c.day_count, on: 'ledger' as const }))),
    ...(l.due ?? []).flatMap((e) => (e.carried ?? []).map((c) => ({ docRef: refOf(c.invoice_line_id), dayCount: c.day_count, on: 'due' as const }))),
  ];
}

// Premier jour d'une interruption : le sien, sinon le premier jour de la fenêtre de facturation, sinon la livraison.
function interruptionStart(l: RentalLineView, i: RentalInterruption): Day | null {
  return i.start ?? l.summary?.window_start ?? deliveredDay(l);
}

// Le jour est dans l'interruption : du premier jour au dernier jour non facturé (end), les deux inclus ; end null = ouverte.
function covers(l: RentalLineView, i: RentalInterruption, day: Day): boolean {
  const start = interruptionStart(l, i);
  return start !== null && start <= day && (i.end === null || day <= i.end);
}

// Interruptions (gestes commerciaux compris : motif offert) qui couvrent le jour.
export function interruptionsAt(l: RentalLineView, day: Day, withCancelled = false): RentalInterruption[] {
  return (l.decisions?.interruptions ?? []).filter((i) => (withCancelled || !i.cancelled) && covers(l, i, day));
}

export function interruptedOn(l: RentalLineView, day: Day): boolean {
  const s = stateAt(l, day);
  return s === 'INTERRUPTED' || s === 'WAITING_START' || s === 'PAID_INTERRUPTED';
}

export interface MemoGroup { key: string; memo: string | null; lines: RentalLineView[]; }

function readable(l: RentalLineView): boolean {
  return l.id > 0 && !l.lifecycle_unreadable && l.decisions != null;
}

export function memoGroupsOf(lines: RentalLineView[]): MemoGroup[] {
  const groups = new Map<string, MemoGroup>();
  for (const l of [...lines].filter((x) => x.typ === 'RENTAL').sort((a, b) => a.seq - b.seq)) {
    const key = normalizeSearch(l.memo);
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { key, memo: key ? (l.memo ?? '').trim() : null, lines: [] }));
    g.lines.push(l);
  }
  return [...groups.values()];
}

// La ligne, puis les autres RENTAL lisibles de même mémo non vide (lit et matelas d'un résident).
export function sameMemoLineIds(l: RentalLineView, lines: RentalLineView[]): number[] {
  const key = normalizeSearch(l.memo);
  if (!key) return [l.id];
  const others = lines.filter((x) => x.id !== l.id && x.typ === 'RENTAL' && readable(x) && normalizeSearch(x.memo) === key).sort((a, b) => a.seq - b.seq);
  return [l.id, ...others.map((x) => x.id)];
}

export function lineBucket(l: RentalLineView): 'to_deliver' | 'in_place' | 'returned' {
  return l.return_realdatdel ? 'returned' : l.realdatdel ? 'in_place' : 'to_deliver';
}

// Interruption posée à l'identique sur plusieurs lignes d'un mémo (gestes commerciaux compris : leur motif est offert).
export interface GroupedDecision {
  key: string; entries: { lineId: number; id: number }[]; sample: RentalInterruption;
  cancelled: boolean; partial: boolean;
  edited: boolean;            // au moins une entrée remplace une entrée annulée (replaces)
  history: { lineId: number; id: number }[]; // entrées remplacées, masquées sur le Planning, citées dans l'infobulle
  missing: number[];          // lignes du groupe livrées pendant elle sans la porter (§7.6 de l'étude)
}

// Identité d'une interruption entre lignes d'un même mémo : mêmes bornes incluses, même motif, même commentaire, même état.
export function sameDecisionKey(d: RentalInterruption): string {
  return JSON.stringify([d.start, d.end, d.reason_id, d.comment ?? '', !!d.cancelled]);
}

export function groupDecisionsOf(g: MemoGroup): GroupedDecision[] {
  const lines = g.lines.filter(readable);
  const byKey = new Map<string, GroupedDecision>();
  for (const l of lines) {
    const ints = l.decisions!.interruptions;
    const replaced = new Set(ints.map((i) => i.replaces).filter((x): x is number => x != null));
    for (const d of ints.filter((i) => !(i.cancelled && replaced.has(i.id)))) {
      const key = sameDecisionKey(d);
      let gd = byKey.get(key);
      if (!gd) byKey.set(key, (gd = { key, entries: [], sample: d, cancelled: !!d.cancelled, partial: false, edited: false, history: [], missing: [] }));
      gd.entries.push({ lineId: l.id, id: d.id });
      for (let prev = d.replaces ?? null, guard = 0; prev != null && guard < 100; guard++) {
        gd.edited = true;
        gd.history.push({ lineId: l.id, id: prev });
        prev = ints.find((i) => i.id === prev)?.replaces ?? null;
      }
    }
  }
  const out = [...byKey.values()];
  for (const gd of out) {
    const carriers = new Set(gd.entries.map((e) => e.lineId));
    gd.partial = carriers.size < lines.length;
    if (gd.cancelled) continue;
    const i = gd.sample;
    const start = i.start ?? minDay(...lines.filter((l) => carriers.has(l.id)).map(deliveredDay));
    gd.missing = lines.filter((l) => {
      const d = deliveredDay(l);
      return !carriers.has(l.id) && d && start && d >= start && (i.end === null || d <= i.end);
    }).map((l) => l.id);
  }
  return out.sort((a, b) => (a.sample.start ?? '').localeCompare(b.sample.start ?? '') || a.key.localeCompare(b.key));
}

// Entrées identiques (sameDecisionKey) des autres lignes RENTAL lisibles du même mémo que la ligne.
export function sameDecisionTargets(lines: RentalLineView[], lineId: number, id: number): { lineId: number; id: number }[] {
  const l = lines.find((x) => x.id === lineId);
  const d = (l?.decisions?.interruptions ?? []).find((x) => x.id === id);
  const memo = normalizeSearch(l?.memo);
  if (!l || !d || !memo) return [];
  const key = sameDecisionKey(d);
  const out: { lineId: number; id: number }[] = [];
  for (const x of lines) {
    if (x.id === lineId || x.typ !== 'RENTAL' || !readable(x) || normalizeSearch(x.memo) !== memo) continue;
    for (const e of x.decisions!.interruptions) if (sameDecisionKey(e) === key) out.push({ lineId: x.id, id: e.id });
  }
  return out;
}

// Ruban d'une décision : du start au end, les deux inclus ; start null = depuis floor (première livraison), end null = ouverte.
export interface RibbonInput { key: string; start: Day | null; end: Day | null; label: string; floor: Day | null; }
// spanLeft / spanWidth : étendue réservée au libellé (%), dans laquelle .anchor-right aligne le libellé à droite.
export interface PlacedRibbon extends Bar { key: string; lane: number; open: boolean; scheduled: boolean; anchorRight: boolean; spanLeft: number; spanWidth: number; }

export function placeRibbons(items: RibbonInput[], w: DayWindow, asOf: Day, ppd: number): { ribbons: PlacedRibbon[]; lanes: number } {
  const placed: Omit<PlacedRibbon, 'lane'>[] = [];
  const reserved: { left: number; width: number }[] = [];
  const windowEndExcl = addDays(w.end, 1); // bord droit de la fenêtre : calcul de positions seulement
  for (const it of items) {
    const start = it.start ?? it.floor;
    if (!start) continue;
    const open = it.end === null;
    const end = it.end ?? (maxDay(addDays(asOf, -1), start) as Day); // ouverte : dessinée jusqu'au trait rouge (début d'as_of), au moins son premier jour
    const bar = spanBar(start, end, w);
    if (!bar) continue;
    const reach = Math.ceil((it.label.length * 6.2 + 26) / Math.max(ppd, 0.01)) + 1;
    const visibleStart = maxDay(start, w.start) as Day;
    const anchorRight = daysBetween(visibleStart, windowEndExcl) < reach;
    const rStart = anchorRight ? (maxDay(addDays(windowEndExcl, -reach), w.start) as Day) : visibleStart;
    const rEndExcl = anchorRight ? windowEndExcl : (maxDay(addDays(visibleStart, reach), minDay(addDays(end, 1), windowEndExcl)) as Day);
    const spanLeft = dayPct(rStart, w);
    const spanWidth = dayPct(rEndExcl, w) - spanLeft;
    reserved.push({ left: spanLeft, width: spanWidth });
    placed.push({ ...bar, key: it.key, open, scheduled: start > asOf, anchorRight, spanLeft, spanWidth });
  }
  const lanes = packRibbonLanes(reserved, Infinity); // sans le plafond de 4 couches des congés RH : deux rubans superposés sont illisibles (JP, 28/10/2026)
  const ribbons = placed.map((p, i) => ({ ...p, lane: lanes[i] }));
  return { ribbons, lanes: ribbons.length ? Math.max(...lanes) + 1 : 0 };
}

// « {motif}[ · {matériels si partiel}] · {du jj/mm | dès la livraison} {au jj/mm | → ?} » ; « au » = dernier jour non facturé (end, inclus).
export function ribbonLabel(d: GroupedDecision, reasonName: string, productNames: string[] | null): string {
  const s = d.sample;
  const who = d.partial && productNames?.length ? ' · ' + productNames.join(', ') : '';
  const startTxt = s.start ? 'du ' + frShort(s.start) : 'dès la livraison';
  const endTxt = s.end ? 'au ' + frShort(s.end) : '→ ?';
  return `${reasonName}${who} · ${startTxt} ${endTxt}`;
}
